import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../utils/api';

interface Trophy {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  trophyType: string;
}

interface TrophyAward {
  id: string;
  trophyId: string;
  userId: string;
  awardedAt: string;
  reason?: string;
  trophy: Trophy;
}

interface TrophyWidgetProps {
  userId?: string; // If not provided, uses current user
  showHeader?: boolean; // Whether to show the "My Trophies" header
  maxTrophies?: number; // Maximum number of trophies to display
}

const TROPHY_TYPE_NAMES = {
  custom: 'Custom Achievement',
  monthly_first: '🥇 Monthly Champion',
  monthly_second: '🥈 Monthly Runner-up', 
  monthly_third: '🥉 Monthly Third Place',
  annual_first: '🏆 Annual Champion',
  annual_second: '🥈 Annual Runner-up',
  annual_third: '🥉 Annual Third Place',
  league_first: '🏆 League Winner',
  league_second: '🥈 League Runner-up',
  league_third: '🥉 League Third Place'
};

const TrophyWidget: React.FC<TrophyWidgetProps> = ({ 
  userId, 
  showHeader = true, 
  maxTrophies = 8 
}) => {
  const [trophyAwards, setTrophyAwards] = useState<TrophyAward[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFeaturedTrophies();
  }, [userId]);

  const fetchFeaturedTrophies = async () => {
    // Check if we have cached data with expiration
    const cacheKey = userId ? `trophy_awards_${userId}` : 'trophy_awards_featured';
    const cacheTimestampKey = userId ? `trophy_awards_timestamp_${userId}` : 'trophy_awards_timestamp_featured';
    
    const cachedData = sessionStorage.getItem(cacheKey);
    const cacheTimestamp = sessionStorage.getItem(cacheTimestampKey);
    
    if (cachedData && cacheTimestamp) {
      try {
        const timestamp = parseInt(cacheTimestamp);
        const now = Date.now();
        const oneHour = 15 * 60 * 1000; // 1 hour in milliseconds
        
        // Check if cache is still valid (less than 1 hour old)
        if (now - timestamp < oneHour) {
          const parsed = JSON.parse(cachedData);
          setTrophyAwards(parsed.slice(0, maxTrophies));
          setLoading(false);
          return;
        } else {
          // Cache expired, remove it
          sessionStorage.removeItem(cacheKey);
          sessionStorage.removeItem(cacheTimestampKey);
        }
      } catch (e) {
        // If parsing fails, remove invalid cache
        sessionStorage.removeItem(cacheKey);
        sessionStorage.removeItem(cacheTimestampKey);
      }
    }
    
    try {
      setLoading(true);
      setError(null);
      
      let endpoint = '/trophies/user/featured';
      if (userId) {
        endpoint = `/trophies/user/${userId}/featured`;
      }

      const response = await apiClient.get(endpoint);
      setTrophyAwards(response.data.slice(0, maxTrophies));
      
      // Cache the data with timestamp
      sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
      sessionStorage.setItem(cacheTimestampKey, Date.now().toString());
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to load trophies');
    } finally {
      setLoading(false);
    }
  };

  const getTrophyTypeIcon = (trophyType: string): string => {
    if (trophyType.includes('first') || trophyType.includes('champion')) return '🏆';
    if (trophyType.includes('second')) return '🥈';
    if (trophyType.includes('third')) return '🥉';
    return '⭐';
  };

  const formatAwardDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto mt-8 px-2 md:px-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-6 w-32"></div>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-6">
            {[...Array(maxTrophies)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-300 dark:bg-gray-600 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return null; // Quietly fail for widgets
  }

  if (trophyAwards.length === 0) {
    return null; // Don't show widget if no trophies
  }

  return (
    <div className="max-w-5xl mx-auto mt-8">
      {showHeader && (
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            {userId ? 'Recent Trophies' : 'My Trophies'}
          </h2>
{/*           <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Latest achievements and awards
          </p> */}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-6 mb-6">
        {trophyAwards.map((award) => (
          <div
            key={award.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200"
          >
            {/* Trophy Image/Icon */}
            <div className="aspect-square flex items-center justify-center p-2 bg-gray-200 dark:bg-gray-700">
              {award.trophy.imageUrl ? (
                <img
                  src={award.trophy.imageUrl}
                  alt={award.trophy.name}
                  className="w-full h-full object-contain max-w-40 max-h-40"
                />
              ) : (
                <div className="text-4xl">
                  {getTrophyTypeIcon(award.trophy.trophyType)}
                </div>
              )}
            </div>

            {/* Trophy Info */}
            <div className="p-4">
              <h3 className="font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>
                {award.trophy.name}
              </h3>
              
              <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>
                {TROPHY_TYPE_NAMES[award.trophy.trophyType as keyof typeof TROPHY_TYPE_NAMES] || 'Achievement'}
              </p>

              {award.reason && (
                <p className="text-xs mb-2 font-medium" style={{ color: 'var(--highlight)' }}>
                  {award.reason}
                </p>
              )}

              <div className="flex items-center text-xs" style={{ color: 'var(--text-secondary)' }}>
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {formatAwardDate(award.awardedAt)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Link to Trophy Room */}
      <div className="text-center">
        <Link 
          to={userId ? `/users/${userId}/trophies` : '/trophies'}
          className="btn-secondary inline-flex items-center px-4 py-2 text-sm"
        >
          View Trophy Room
        </Link>
      </div>
    </div>
  );
};

export default TrophyWidget;